/**
 * 「打回重做」的**唯一**弹窗 —— 「今天的作业」与「超时」两个视图共用。
 *
 * ## 为什么它必须与「通过」住在同一个文件夹、由同一个 hook 渲染
 *
 * 2026-10-15 用户实测报的 bug 是"超时列表里 submitted 的项没有通过/打回按钮"。
 * 修法不能是"在超时列表里再写一份弹窗"：打回这件事有三处会漂移 ——
 * 原因上限、原因会写进孩子端（`checkin.note`）、失败时怎么收场。
 * 所以**写操作与缓存失效**在 `lib/homeworkApproval.tsx`（`useHomeworkApprovalActions`），
 * 弹窗的**样子**在这里，两处只有这一份。
 *
 * ## 三条不能改的语义（与后端 `rejectCheckin()` 一一对应）
 *
 * 1. 打回后这一项回到**未完成**（`submitted → pending`），孩子要重做再交一次；
 * 2. 打回**不计分**（提交阶段本来就没计分）；
 * 3. 原因**可选**：填了会同时进事件流（不可变留档，含被打回前的备注）与
 *    `checkin.note`（孩子端卡片上直接显示）；**留空时不动孩子的原备注**。
 *    这一点必须在 `hint` 里写出来，否则家长会以为"不写点什么会把孩子的备注清掉"。
 */
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';

/** 打回弹窗要显示的上下文（只有展示用的名字，写操作由调用方负责）。 */
export interface HomeworkRejectTarget {
  /** 这一项作业的名字（标题下面那行小字）。 */
  name: string;
  /**
   * 非空 = 标题下面补一行"属于哪一天"。
   *
   * ⚠️ 「超时」是跨天视图：一条昨天提交的项如果不写出属于哪一天，
   * 家长看到的就是一个"没有出处"的名字。
   */
  date?: string;
}

export interface HomeworkApprovalRejectDialogProps {
  /** `null` = 不显示（弹窗关闭）。 */
  target: HomeworkRejectTarget | null;
  reason: string;
  onReasonChange: (reason: string) => void;
  /** 上一次打回失败的人话原因（`null` = 没有）。 */
  error: string | null;
  pending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function HomeworkApprovalRejectDialog({
  target,
  reason,
  onReasonChange,
  error,
  pending,
  onClose,
  onConfirm,
}: HomeworkApprovalRejectDialogProps) {
  return (
    <Modal
      open={target !== null}
      onClose={onClose}
      title="打回重做？"
      description={
        target ? [target.name, target.date ? `${target.date} 的作业` : ''].filter(Boolean).join(' · ') : undefined
      }
      size="sm"
      footer={
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:gap-3">
          <Button variant="secondary" className="sm:flex-1" onClick={onClose}>
            再想想
          </Button>
          <Button variant="danger" className="sm:flex-1" loading={pending} onClick={onConfirm}>
            打回让孩子重做
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800">
          打回后这一项会回到<strong>未完成</strong>，<strong>不会计分</strong>，孩子要重做再交一次。
        </p>
        <Input
          label="要改什么（可选，会显示给孩子）"
          maxLength={500}
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          placeholder="例如：字写得太潦草，重写一遍"
          hint="留空也可以 —— 留空时不会覆盖孩子原来写的备注"
        />
        {error && (
          <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
